/**
 * CompletedClassesTab — dedicated home for past classes with attendance + recording link.
 * Full-admin + super-admin only (sub-admin doesn't see this tab).
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { CheckCircle2, Users2, Loader2, ChevronLeft, ChevronRight, Filter, PlayCircle, AlertCircle, ExternalLink, Archive, Eye, EyeOff } from 'lucide-react';
import PublishArchiveModal from './PublishArchiveModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GRADE_OPTIONS = ['MEO Class 1','MEO Class 2','MEO Class 4','ETO','Master','Chief Mate','2MFG'];
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const fmtIST = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; }
};

const CompletedClassesTab = () => {
  const [classes, setClasses] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [gradeFilter, setGradeFilter] = useState('all');
  const [hostFilter, setHostFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [readyOnly, setReadyOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Attendance drawer state
  const [attClass, setAttClass] = useState(null);
  const [attData, setAttData] = useState(null);
  const [attLoading, setAttLoading] = useState(false);
  const [attError, setAttError] = useState('');

  // Archive publish modal state
  const [archiveClass, setArchiveClass] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const [cls, hostsResp] = await Promise.all([
        axios.get(`${API}/admin/live-classes`),
        axios.get(`${API}/admin/live-classes/hosts`),
      ]);
      setClasses((cls.data || []).filter(c => c.status === 'completed'));
      setHosts(hostsResp.data || []);
    } catch (err) {
      toast.error('Failed to load completed classes');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [gradeFilter, hostFilter, dateFrom, dateTo, readyOnly]);

  const readyPendingCount = useMemo(
    () => classes.filter(c => c.qna_ready_at && !c.published_at).length,
    [classes],
  );

  const filtered = useMemo(() => {
    const fromTs = dateFrom ? new Date(`${dateFrom}T00:00:00+05:30`).getTime() : null;
    const toTs = dateTo ? new Date(`${dateTo}T23:59:59+05:30`).getTime() : null;
    return classes
      .filter(c => gradeFilter === 'all' ? true : (Array.isArray(c.grades) && c.grades.includes(gradeFilter)))
      .filter(c => hostFilter === 'all' ? true : c.host_user_id === hostFilter)
      .filter(c => !readyOnly || (c.qna_ready_at && !c.published_at))
      .filter(c => {
        const t = new Date(c.start_time).getTime();
        if (fromTs && t < fromTs) return false;
        if (toTs && t > toTs) return false;
        return true;
      })
      .sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
  }, [classes, gradeFilter, hostFilter, dateFrom, dateTo, readyOnly]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paged = useMemo(() => filtered.slice((safePage - 1) * pageSize, safePage * pageSize), [filtered, safePage, pageSize]);

  const activeFilterCount = (gradeFilter !== 'all' ? 1 : 0) + (hostFilter !== 'all' ? 1 : 0) + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0) + (readyOnly ? 1 : 0);
  const clearFilters = () => { setGradeFilter('all'); setHostFilter('all'); setDateFrom(''); setDateTo(''); setReadyOnly(false); };

  const openAttendance = async (cls) => {
    setAttClass(cls); setAttData(null); setAttError(''); setAttLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/live-classes/${cls.id}/attendance`);
      setAttData(data);
    } catch (err) {
      // Backend now returns either a plain string (older paths) or a
      // structured { code, message } object for typed Zoom failures.
      const d = err.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d?.message || 'Failed to load attendance');
      setAttError(msg);
    } finally { setAttLoading(false); }
  };

  return (
    <>
      <Card data-testid="completed-classes-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5" />
            Completed Classes
          </CardTitle>
          <CardDescription className="mt-2">
            History of past classes. Click the attendance icon to see who joined and how long they stayed. Paste a recording URL to save it against the class.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filter bar */}
          <div className="flex flex-wrap items-center gap-3 pb-4 mb-4 border-b">
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Filter className="w-3.5 h-3.5" />
              <span className="font-semibold uppercase tracking-wide">Filters</span>
              {activeFilterCount > 0 && <Badge variant="secondary" className="ml-1">{activeFilterCount}</Badge>}
            </div>
            <Select value={gradeFilter} onValueChange={setGradeFilter}>
              <SelectTrigger className="h-9 w-40" data-testid="completed-filter-grade"><SelectValue placeholder="Grade" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All grades</SelectItem>
                {GRADE_OPTIONS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={hostFilter} onValueChange={setHostFilter}>
              <SelectTrigger className="h-9 w-52" data-testid="completed-filter-host"><SelectValue placeholder="Host" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All hosts</SelectItem>
                {hosts.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1.5 pl-2 border-l">
              <Label className="text-xs text-gray-500">From</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-36" data-testid="completed-filter-date-from" />
              <Label className="text-xs text-gray-500 ml-1">To</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} min={dateFrom || undefined} className="h-9 w-36" data-testid="completed-filter-date-to" />
            </div>
            <button
              type="button"
              onClick={() => setReadyOnly(v => !v)}
              className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-xs font-semibold transition-colors ${readyOnly
                ? 'bg-emerald-600 border-emerald-700 text-white hover:bg-emerald-700'
                : 'bg-white border-emerald-300 text-emerald-800 hover:bg-emerald-50'}`}
              title={readyOnly
                ? 'Showing only classes marked ready by their faculty but not yet published.'
                : 'Filter to just the classes waiting for your review.'}
              data-testid="completed-filter-ready-only"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${readyOnly ? 'bg-white' : 'bg-emerald-500'}`} />
              Ready to publish
              {readyPendingCount > 0 && (
                <span
                  className={`ml-1 tabular-nums px-1.5 rounded-full text-[10px] ${readyOnly ? 'bg-white/25 text-white' : 'bg-emerald-100 text-emerald-900'}`}
                  data-testid="completed-filter-ready-count"
                >
                  {readyPendingCount}
                </span>
              )}
            </button>
            {activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 text-xs">Clear all</Button>
            )}
            <div className="ml-auto text-xs text-gray-500">{filtered.length} {filtered.length === 1 ? 'class' : 'classes'}</div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No completed classes match your filters.</p>
            </div>
          ) : (
            <>
              <Table data-testid="completed-classes-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Start (IST)</TableHead>
                    <TableHead>Host</TableHead>
                    <TableHead>Grade</TableHead>
                    <TableHead>Recording</TableHead>
                    <TableHead>Archive</TableHead>
                    <TableHead className="text-right">Attendance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paged.map(c => {
                    return (
                      <TableRow key={c.id} data-testid={`completed-row-${c.id}`}>
                        <TableCell className="font-medium">{c.title}</TableCell>
                        <TableCell className="text-sm">{fmtIST(c.start_time)}</TableCell>
                        <TableCell className="text-sm">{c.host_user_name}</TableCell>
                        <TableCell className="text-xs">
                          {Array.isArray(c.grades) && c.grades.length > 0
                            ? c.grades.join(', ')
                            : <span className="text-gray-400">—</span>}
                        </TableCell>
                        <TableCell>
                          {/* Recording URL is captured / edited entirely
                              via the archive modal now — showing a bare
                              read-only link here (or a "not set" hint) so
                              admins have one clear entry point (the Edit
                              Archive button) instead of two ambiguous ones. */}
                          {c.recording_url ? (
                            <a
                              href={c.recording_url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-[#0A7EA4] hover:underline text-xs"
                              data-testid={`completed-recording-link-${c.id}`}
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                              Open
                            </a>
                          ) : (
                            <span className="text-xs text-gray-400" data-testid={`completed-recording-empty-${c.id}`}>
                              Not set
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant={c.published_at ? 'default' : 'outline'}
                              onClick={() => setArchiveClass(c)}
                              className={c.published_at
                                ? 'bg-emerald-600 hover:bg-emerald-700 text-white gap-1'
                                : 'gap-1'}
                              data-testid={`completed-archive-btn-${c.id}`}
                              title={c.published_at
                                ? 'Edit or unpublish archive'
                                : 'Open the archive editor (add recording + summary, then publish)'}
                            >
                              {c.published_at ? (
                                <><Eye className="w-3.5 h-3.5" /> Published</>
                              ) : (
                                <><Archive className="w-3.5 h-3.5" /> Edit Archive</>
                              )}
                            </Button>
                            {c.qna_ready_at && !c.published_at && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-300 text-[10px] font-semibold text-emerald-800"
                                title={`Q&A curator ${c.qna_ready_by_name || ''} marked this ready for archive at ${new Date(c.qna_ready_at).toLocaleString('en-IN')}`}
                                data-testid={`completed-qna-ready-dot-${c.id}`}
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                Ready
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" onClick={() => openAttendance(c)}
                            data-testid={`completed-attendance-btn-${c.id}`}>
                            <Users2 className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4 pt-3 border-t flex-wrap gap-3">
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <span>Rows per page</span>
                  <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(parseInt(v)); setPage(1); }}>
                    <SelectTrigger className="h-8 w-20"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAGE_SIZE_OPTIONS.map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                  </Select>
                  <span className="ml-3">Showing <strong>{filtered.length === 0 ? 0 : (safePage - 1) * pageSize + 1}</strong>&ndash;<strong>{Math.min(safePage * pageSize, filtered.length)}</strong> of <strong>{filtered.length}</strong></span>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage <= 1}>
                    <ChevronLeft className="w-4 h-4 mr-1" />Prev
                  </Button>
                  <span className="text-xs px-3 tabular-nums whitespace-nowrap">Page {safePage} of {totalPages}</span>
                  <Button size="sm" variant="outline" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}>
                    Next<ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Attendance drawer */}
      <Sheet open={!!attClass} onOpenChange={(v) => { if (!v) { setAttClass(null); setAttData(null); setAttError(''); } }}>
        <SheetContent className="sm:max-w-2xl overflow-y-auto" data-testid="attendance-drawer">
          <SheetHeader className="text-left">
            <SheetTitle className="flex items-center gap-2">
              <Users2 className="w-5 h-5" />
              Attendance
            </SheetTitle>
            <SheetDescription>{attClass?.title} &middot; {attClass && fmtIST(attClass.start_time)}</SheetDescription>
          </SheetHeader>

          {attLoading && (
            <div className="flex flex-col items-center py-16 gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              <p className="text-sm text-gray-500">Fetching from Zoom&hellip;</p>
            </div>
          )}

          {attError && (
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900">
                  <p className="font-semibold mb-1">Cannot load attendance</p>
                  <p>{attError}</p>
                  {attError.toLowerCase().includes('scope') && (
                    <p className="mt-2 text-xs">Open the <strong>Zoom Setup Guide</strong> from the Zoom Licenses tab &rarr; S2S OAuth &rarr; step 5 &mdash; find <code className="px-1 bg-white rounded">report:read:admin</code> in the scopes list.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {attData && (
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-700">Unique attendees</p>
                  <p className="text-2xl font-bold text-blue-900">{attData.total_unique_participants}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-xs text-green-700">Total attendance</p>
                  <p className="text-2xl font-bold text-green-900">{attData.total_attendance_minutes} min</p>
                </div>
              </div>

              {/* Level-2 registration audit — only shown when Zoom registration
                  is enabled on this meeting (i.e. created after Feb 2026). */}
              {attData.registration?.enabled && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 space-y-2" data-testid="attendance-registration-audit">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-semibold text-emerald-900 flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      Registration audit
                    </div>
                    <div className="text-[11px] text-emerald-900/80 tabular-nums">
                      <span className="font-semibold">{attData.registration.total_registered}</span> registered ·{' '}
                      <span className="font-semibold">{attData.total_unique_participants}</span> joined ·{' '}
                      <span className="font-semibold text-amber-700">{attData.registration.no_shows.length}</span> no-shows
                    </div>
                  </div>
                  {attData.registration.no_shows.length > 0 && (
                    <details className="text-xs" data-testid="attendance-no-shows">
                      <summary className="cursor-pointer text-amber-800 font-medium hover:text-amber-900">
                        {attData.registration.no_shows.length} paid user{attData.registration.no_shows.length === 1 ? '' : 's'} registered but never joined
                      </summary>
                      <ul className="mt-1.5 pl-4 space-y-0.5 text-gray-700">
                        {attData.registration.no_shows.slice(0, 50).map((ns, i) => (
                          <li key={i} className="tabular-nums" data-testid={`attendance-no-show-${i}`}>
                            {ns.name}{ns.email && ns.email !== ns.name ? ` — ${ns.email}` : ''}
                          </li>
                        ))}
                        {attData.registration.no_shows.length > 50 && (
                          <li className="text-gray-400">…and {attData.registration.no_shows.length - 50} more</li>
                        )}
                      </ul>
                    </details>
                  )}
                  {attData.registration.unregistered_joiners.length > 0 && (
                    <details className="text-xs" data-testid="attendance-unregistered-joiners">
                      <summary className="cursor-pointer text-red-700 font-medium hover:text-red-800">
                        ⚠ {attData.registration.unregistered_joiners.length} joined without registering
                      </summary>
                      <ul className="mt-1.5 pl-4 space-y-0.5 text-gray-700">
                        {attData.registration.unregistered_joiners.slice(0, 20).map((u, i) => (
                          <li key={i}>{u.name}{u.email ? ` — ${u.email}` : ''}</li>
                        ))}
                      </ul>
                      <p className="mt-1 text-[10px] text-gray-500 italic">
                        Usually the class host (they don&apos;t register themselves). Investigate any unexpected names.
                      </p>
                    </details>
                  )}
                </div>
              )}

              {attData.participants.length === 0 ? (
                <p className="text-center py-8 text-sm text-gray-500">No participants recorded for this class.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      {attData.registration?.enabled && <TableHead>Reg?</TableHead>}
                      <TableHead className="text-right">Joins</TableHead>
                      <TableHead className="text-right">Minutes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {attData.participants.map((p, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-sm font-medium">{p.name}</TableCell>
                        <TableCell className="text-xs text-gray-500">{p.email || '—'}</TableCell>
                        {attData.registration?.enabled && (
                          <TableCell>
                            {p.registration_status === 'registered' ? (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-800" title="Paid user, registered via MARQ">
                                ✓
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800" title="Joined without going through MARQ registration — usually the host">
                                —
                              </span>
                            )}
                          </TableCell>
                        )}
                        <TableCell className="text-right text-sm">{p.join_count}</TableCell>
                        <TableCell className="text-right text-sm font-mono">{p.total_minutes}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              <p className="text-xs text-gray-400 text-right">Fetched {new Date(attData.fetched_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}. Cached 24h.</p>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Publish archive dialog */}
      <PublishArchiveModal
        open={!!archiveClass}
        cls={archiveClass}
        onClose={() => setArchiveClass(null)}
        onSaved={(updated) => {
          // Merge the updated class back into the list so the badge flips instantly
          setClasses(list => list.map(c => c.id === updated.id ? { ...c, ...updated } : c));
          setArchiveClass(prev => prev ? { ...prev, ...updated } : prev);
        }}
      />
    </>
  );
};

export default CompletedClassesTab;
